import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "co_cart_v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.product.id === product.id);
            if (existing) {
                return prev.map((i) =>
                    i.product.id === product.id
                        ? { ...i, quantity: i.quantity + quantity }
                        : i,
                );
            }
            return [...prev, { product, quantity }];
        });
        setOpen(true);
    };

    const removeItem = (productId) => {
        setItems((prev) => prev.filter((i) => i.product.id !== productId));
    };

    const updateQty = (productId, quantity) => {
        if (quantity <= 0) return removeItem(productId);
        setItems((prev) =>
            prev.map((i) =>
                i.product.id === productId ? { ...i, quantity } : i,
            ),
        );
    };

    const clear = () => setItems([]);

    const totals = useMemo(() => {
        const count = items.reduce((s, i) => s + i.quantity, 0);
        const subtotal = items.reduce(
            (s, i) => s + i.quantity * i.product.price,
            0,
        );
        return { count, subtotal };
    }, [items]);

    return (
        <CartContext.Provider
            value={{
                items,
                addItem,
                removeItem,
                updateQty,
                clear,
                open,
                setOpen,
                ...totals,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
